import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  MenuItem,
  Typography,
} from "@mui/material";
import { StyledMenu } from "@/components/styled";
import { capitalizeFirstLetter } from "@/utils/capitalizeFirstLetter.ts";
import SearchInput from "@/components/SearchInput";
import SVGSelectArrowDown from "@/assets/icons/SVGSelectArrowDown.tsx";
import {
  Control,
  Controller,
  ControllerProps,
  useFormContext,
} from "react-hook-form";
import useDebouncedSearch from "@/hooks/useDebouncedSearch.ts";

type Props = {
  getItems: (e?: any) => Promise<any>;
  name: string;
  control: Control<any, any>;
  controlProps?: Partial<ControllerProps>;
  label?: string;
  itemsKey: string;
  idKey: string;
  placeholder: string;
  disabled?: boolean;
  onValueChange?: () => void;
};

const AsyncSelectWithSearch = ({
  getItems,
  name,
  control,
  controlProps,
  label,
  itemsKey,
  placeholder,
  idKey,
  disabled = false,
  onValueChange,
}: Props) => {
  const { setValue, getValues } = useFormContext();

  const [items, setItems] = useState<{ id: number; name: string }[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const isRequired = !!controlProps?.rules?.required;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { searchValue, handleDebounceSearchValue } = useDebouncedSearch(
    () => {},
  );
  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response = await getItems(searchValue);
        setItems(response?.data?.[itemsKey]);
      } catch (e) {
        console.error(e);
      }
    };
    fetchItems();
  }, [searchValue, getItems]);

  return (
    <FormControl
      component="fieldset"
      sx={{ width: "100%", paddingBottom: "24px" }}
    >
      <FormLabel component="legend">
        <Box
          sx={{
            fontSize: "12px",
            fontWeight: 500,
            color: "#373533",
            paddingBottom: "2px",
            display: "flex",
          }}
        >
          {label}
          {isRequired && (
            <Typography sx={{ color: "red", paddingLeft: "4px" }}>*</Typography>
          )}
        </Box>
      </FormLabel>
      <Controller
        name={name}
        control={control}
        {...controlProps}
        render={({ field: { onChange, value } }) => (
          <>
            <Button
              variant="outlinedGray"
              endIcon={<SVGSelectArrowDown />}
              sx={{
                textTransform: "none",
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
              }}
              disabled={disabled}
              onClick={handleClick}
            >
              {value?.length > 0 ? (
                <Typography
                  variant={"subtitleRegular"}
                  sx={{
                    color: "textGray2",
                    ...(disabled && { color: "rgb(0 0 0 / 38%)" }),
                  }}
                >
                  {value}
                </Typography>
              ) : (
                placeholder
              )}
            </Button>
            <StyledMenu
              id="basic-menu"
              anchorEl={anchorEl}
              sx={{
                "& .MuiPaper-root": { width: anchorEl?.offsetWidth },
              }}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              <Box sx={{ padding: "0 16px 8px 16px" }}>
                <SearchInput onDebounce={handleDebounceSearchValue} />
              </Box>
              <Box sx={{ overflow: "auto", maxHeight: "200px" }}>
                {items?.map((el) => {
                  const isSelected = getValues(idKey) === el?.id;
                  return (
                    <MenuItem
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                        maxHeight: "48px",
                      }}
                      key={el?.id}
                      onClick={() => {
                        if (isSelected) {
                          onChange(""); // Clear the value
                          setValue(idKey, null);
                        } else {
                          onChange(el?.name);
                          setValue(idKey, el?.id);
                        }
                        onValueChange?.();
                        handleClose();
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          gap: "10px",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#4E4B48",
                          }}
                        >
                          {capitalizeFirstLetter(el?.name ?? "")}
                        </Typography>
                        <Checkbox checked={getValues(idKey) === el?.id} />
                      </Box>
                    </MenuItem>
                  );
                })}
              </Box>
            </StyledMenu>
          </>
        )}
      />
    </FormControl>
  );
};

export default AsyncSelectWithSearch;
